"use client";

import { absoluteCmsUrl, BaseImage } from "../base-image/BaseImage";
import React, { useEffect, useRef, useState } from "react";
import { HeroImageProps } from "./types";

type HeroMediaProps = {
  media?: HeroImageProps["media"] | null;
  imagePosition?: string | null;
  className?: string;
};

export const HeroImage: React.FC<HeroMediaProps> = ({
  media,
  className,
  imagePosition,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  const mediaMime = (media as any)?.mime as string | undefined;
  const isVideo =
    !!mediaMime?.startsWith("video/") ||
    /\.(mp4|webm|ogg)$/i.test(media?.url || "");

  const rawImageSrc =
    media?.url ||
    media?.formats?.large?.url ||
    media?.formats?.medium?.url ||
    media?.formats?.small?.url ||
    "";

  const src = absoluteCmsUrl(rawImageSrc);
  const poster = absoluteCmsUrl(
    media?.formats?.thumbnail?.url || media?.formats?.small?.url || "",
  );
  const alt =
    media?.alternativeText ||
    media?.caption ||
    media?.name ||
    (isVideo ? "Hero video" : "Hero image");

  // Intersection observer for lazy loading video
  useEffect(() => {
    if (!isVideo || !src) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoadVideo(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '100px' }
    );

    const videoContainer = document.querySelector('.hero-image-container');
    if (videoContainer) {
      observer.observe(videoContainer);
    }

    return () => observer.disconnect();
  }, [isVideo, src]);

  useEffect(() => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v) return;
    
    // Set privacy-focused attributes
    v.setAttribute('referrerpolicy', 'no-referrer');
    
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => {
      if (m.matches) {
        v.pause();
        v.removeAttribute("autoplay");
        v.removeAttribute("loop");
      }
    };
    apply();
    try {
      m.addEventListener("change", apply);
    } catch {
      m.addListener(apply);
    }
    return () => {
      try {
        m.removeEventListener("change", apply);
      } catch {
        m.removeListener(apply);
      }
    };
  }, [isVideo]);

  if (!src) return null;

  return isVideo ? (
    <div className="hero-image-container">
      {shouldLoadVideo ? (
        <video
          ref={videoRef}
          className={className ?? "absolute inset-0 w-full h-full object-cover"}
          src={src}
          poster={poster || undefined}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-label={alt}
          crossOrigin="anonymous"
        >
          <track 
            kind="captions" 
            srcLang={media?.captions?.srcLang || "en"} 
            label={media?.captions?.label || "English captions"} 
            src={media?.captions?.url ? absoluteCmsUrl(media.captions.url) || "" : ""} 
          />
        </video>
      ) : (
        <BaseImage
          src={poster || src}
          alt={alt}
          fill
          sizes="100vw"
          unoptimized
          imagePosition={imagePosition}
          className={className ?? "absolute inset-0 w-full h-full object-cover"}
          isHero={true}
          isCritical={true}
          isAboveFold={true}
          breakpoint="full"
        />
      )}
    </div>
  ) : (
    <BaseImage
      src={src}
      alt={alt}
      fill
      sizes="100vw"
      unoptimized
      imagePosition={imagePosition}
      className={className ?? "absolute inset-0 w-full h-full"}
      isHero={true}
      isCritical={true}
      isAboveFold={true}
      breakpoint="full"
    />
  );
};
